/**
 * CR314: Hook to load category icons once for efficient lookup
 * Returns a map of category name -> icon URL
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CategoryIconData {
  id: string;
  name: string;
  icon_url: string | null;
  icon_svg: string | null;
}

/**
 * Fetch all categories and build a name->icon map
 * Used for category icon fallback in Merchant Directory
 */
export function useCategoryIconMap() {
  return useQuery({
    queryKey: ["category-icon-map"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("merchant_categories")
        .select("id, name, icon_url, icon_svg");

      if (error) throw error;

      // Build map keyed by category name (case-insensitive)
      const map = new Map<string, CategoryIconData>();
      for (const cat of data ?? []) {
        map.set(cat.name.toLowerCase(), cat as CategoryIconData);
      }
      return map;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - categories rarely change
  });
}

/**
 * Get icon for a category name from the map
 */
export function getCategoryIconFromMap(
  map: Map<string, CategoryIconData> | undefined,
  categoryName: string | null | undefined
): CategoryIconData | null {
  if (!map || !categoryName) return null;
  return map.get(categoryName.toLowerCase()) ?? null;
}
